// client/src/components/PalletCalculatorModal.js
import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap';

export default function PalletCalculatorModal({ show, handleClose }) {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');

  // Helper to format numbers with commas and 2 decimals
  const formatNumber = (num) => {
    if (isNaN(num) || num === null || num === undefined) return '';
    return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Helper to format numbers with commas and NO decimals
  const formatInteger = (num) => {
    if (isNaN(num) || num === null || num === undefined) return '';
    // Use maximumFractionDigits: 0 to remove decimals
    return Math.round(Number(num)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const calculations = useMemo(() => {
    const l = parseFloat(length);
    const w = parseFloat(width);
    const h = parseFloat(height);
    const wt = parseFloat(weight);

    // If any value is missing or invalid, return null
    if ([l, w, h, wt].some(isNaN)) return null;

    const lxhxw = l * w * h;
    const lxhxwDiv1728 = lxhxw / 1728;
    
    // Prevent division by zero if calculated volume is 0
    if (lxhxwDiv1728 === 0) return null; 
    
    const result = wt / lxhxwDiv1728;

    // Determine Sub and Class
    let sub, cls;
    if (result < 1) { sub = 1; cls = 400; }
    else if (result < 2) { sub = 2; cls = 300; }
    else if (result < 4) { sub = 3; cls = 250; }
    else if (result < 6) { sub = 4; cls = 175; }
    else if (result < 8) { sub = 5; cls = 125; }
    else if (result < 10) { sub = 6; cls = 100; }
    else if (result < 12) { sub = 7; cls = 92.5; }
    else if (result < 15) { sub = 8; cls = 85; }
    else if (result < 22.5) { sub = 9; cls = 70; }
    else if (result < 30) { sub = 10; cls = 65; }
    else { sub = 11; cls = 60; }

    return {
      l, w, h, wt,
      lxhxw,
      lxhxwDiv1728,
      result,
      sub,
      cls
    };
  }, [length, width, height, weight]);

  const handleReset = () => {
    setLength('');
    setWidth('');
    setHeight('');
    setWeight('');
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Pallet Calculator</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Row className="mb-2">
            <Col>
              <Form.Group controlId="lengthInput">
                <Form.Label>Length</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter length"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="widthInput">
                <Form.Label>Width</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter width"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>
              <Form.Group controlId="heightInput">
                <Form.Label>Height</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter height"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="weightInput">
                <Form.Label>Weight</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter weight"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>

        <div className="mt-3">
          <h6>Calculations:</h6>
          <Table bordered size="sm" style={{ tableLayout: 'fixed' }}>
            <tbody>
              <tr>
                <td style={{ width: '50%' }}>L x W x H</td>
                <td style={{ width: '50%' }}>
                  {calculations ? formatInteger(calculations.lxhxw) : ''}
                </td>
              </tr>
              <tr>
                <td>L x W x H / 1728</td>
                <td>{calculations ? formatNumber(calculations.lxhxwDiv1728) : ''}</td>
              </tr>
              <tr>
                <td>Result</td>
                <td>{calculations ? formatNumber(calculations.result) : ''}</td>
              </tr>
              <tr>
                <td>Sub / Class</td>
                <td>
                  {calculations 
                    ? `Sub ${calculations.sub} / Class ${calculations.cls}` 
                    : ''
                  }
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-primary" onClick={handleReset}>Reset</Button>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}